class AlertService {
  constructor() {
    this.alerts = new Map();
    this.isRunning = false;
  }

  addAlert(tradeId, trade) {
    if (!trade.enableAlerts || !['ANALYSIS', 'OPEN'].includes(trade.status)) {
      return;
    }

    const entryPrice = parseFloat(trade.entryPrice);
    const stopLoss = parseFloat(trade.stopLoss);
    const takeProfit = parseFloat(trade.exitPrice);
    
    if (!entryPrice) return;

    const alertConfig = {
      tradeId,
      symbol: trade.symbol,
      direction: trade.direction || 'LONG',
      entryPrice,
      stopLoss,
      takeProfit,
      status: trade.status,
      alerts: {
        buyZone: trade.status === 'ANALYSIS' && entryPrice,
        takeProfit: trade.status === 'OPEN' && takeProfit,
        stopLoss: trade.status === 'OPEN' && stopLoss
      }
    };

    this.alerts.set(tradeId, alertConfig);
  }

  removeAlert(tradeId) {
    this.alerts.delete(tradeId);
  }

  checkPriceAlerts(livePrices) {
    if (this.alerts.size === 0) return;

    this.alerts.forEach((alert, tradeId) => {
      const priceData = this.getPriceData(alert.symbol, livePrices);
      if (!priceData) return;

      const currentPrice = priceData.price;
      
      // Buy Zone Alert (for ANALYSIS trades)
      if (alert.status === 'ANALYSIS') {
        const tolerance = alert.entryPrice * 0.01;
        const diff = Math.abs(currentPrice - alert.entryPrice);
        
        if (alert.alerts.buyZone && diff <= tolerance) {
          this.showNotification(
            `🎯 Buy Zone Alert`,
            `${alert.symbol} is near entry price: $${currentPrice.toFixed(4)} (Target: $${alert.entryPrice})`
          );
          alert.alerts.buyZone = false;
        }
      }

      // Take Profit Alert (for OPEN trades)
      if (alert.alerts.takeProfit && alert.status === 'OPEN') {
        const hitTP = alert.direction === 'LONG' 
          ? currentPrice >= alert.takeProfit
          : currentPrice <= alert.takeProfit;
        
        if (hitTP) {
          this.showNotification(
            `🎉 Take Profit Hit!`,
            `${alert.symbol} reached TP: $${currentPrice.toFixed(4)} (Target: $${alert.takeProfit})`
          );
          alert.alerts.takeProfit = false;
        }
      }

      // Stop Loss Alert (for OPEN trades)
      if (alert.alerts.stopLoss && alert.status === 'OPEN') {
        const hitSL = alert.direction === 'LONG'
          ? currentPrice <= alert.stopLoss
          : currentPrice >= alert.stopLoss;
        
        if (hitSL) {
          this.showNotification(
            `⚠️ Stop Loss Hit!`,
            `${alert.symbol} hit SL: $${currentPrice.toFixed(4)} (SL: $${alert.stopLoss})`
          );
          alert.alerts.stopLoss = false;
        }
      }
    });
  }

  getPriceData(symbol, livePrices) {
    // Try direct match first
    if (livePrices[symbol]) {
      return livePrices[symbol];
    }
    
    // Try normalized symbol
    const normalizedSymbol = this.normalizeSymbol(symbol);
    if (livePrices[normalizedSymbol]) {
      return livePrices[normalizedSymbol];
    }
    
    // Special cases
    if (symbol === 'APT/USDT' && livePrices['APT']) {
      return livePrices['APT'];
    }
    if ((symbol === 'HYPEUSDT' || symbol === 'HYPE/USDT') && livePrices['HYPE']) {
      return livePrices['HYPE'];
    }
    if ((symbol === '1000PEPE/USDT' || symbol === '1000PEPE') && livePrices['PEPE']) {
      return livePrices['PEPE'];
    }
    
    return null;
  }

  normalizeSymbol(inputStr) {
    if (!inputStr) return '';
    
    if (inputStr.startsWith('1000')) {
      return inputStr.substring(4);
    }
    
    let clean = inputStr.toLowerCase().replace(/[\/\-_\s]/g, '');
    clean = clean.replace('usdt', '').replace('usd', '').replace('busd', '');
    return clean.toUpperCase();
  }

  showNotification(title, body) {
    if ('Notification' in window) {
      if (Notification.permission === 'granted') {
        new Notification(title, {
          body,
          icon: '/favicon.ico'
        });
      } else if (Notification.permission === 'default') {
        Notification.requestPermission().then(permission => {
          if (permission === 'granted') {
            new Notification(title, {
              body,
              icon: '/favicon.ico'
            });
          }
        });
      }
    }
  }

  updateAlerts(trades) {
    this.alerts.clear();
    
    trades.forEach(trade => {
      if (trade.enableAlerts && ['ANALYSIS', 'OPEN'].includes(trade.status)) {
        this.addAlert(trade.tradeId, trade);
      }
    });
  }
}

const alertServiceInstance = new AlertService();

// Request notification permission on load
if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
  Notification.requestPermission();
}

export default alertServiceInstance;